import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Save, RotateCcw, Plus, Trash2, Eye, ChevronUp, ChevronDown, FileDown } from 'lucide-react';

/**
 * Editor for the Terms of Sale Agreement letter that ships with every quote.
 *
 * Stored under `report_settings.terms_of_trade`. Operators can:
 *  - edit / re-order / add / remove sections
 *  - rewrite the Quotation Acceptance block (with {client_name}, {property_address},
 *    {quote_total}, {appraisal_no}, {date} placeholders)
 *  - toggle whether terms are auto-appended to the Quotation PDF
 *  - reset everything back to the verbatim legacy text
 */
export default function TermsOfTradeEditor() {
  const { api } = useAuth();
  const [terms, setTerms] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const previewObjectUrl = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get('/report-settings/terms-of-trade');
    setTerms(data.terms);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // Cleanup any preview blob URLs when unmounting
  useEffect(() => () => {
    if (previewObjectUrl.current) URL.revokeObjectURL(previewObjectUrl.current);
  }, []);

  const setField = (k, v) => setTerms(t => ({ ...t, [k]: v }));

  const setSection = (idx, patch) => {
    setTerms(t => ({
      ...t,
      sections: t.sections.map((s, i) => i === idx ? { ...s, ...patch } : s),
    }));
  };

  const moveSection = (idx, dir) => {
    setTerms(t => {
      const next = [...t.sections];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return t;
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...t, sections: next };
    });
  };

  const removeSection = (idx) => {
    if (!window.confirm('Remove this section?')) return;
    setTerms(t => ({ ...t, sections: t.sections.filter((_, i) => i !== idx) }));
  };

  const addSection = () => {
    setTerms(t => ({
      ...t,
      sections: [...t.sections, { heading: 'NEW SECTION', body: '' }],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title: terms.title,
        intro: terms.intro,
        sections: terms.sections,
        acceptance_heading: terms.acceptance_heading,
        acceptance_subheading: terms.acceptance_subheading,
        acceptance_body: terms.acceptance_body,
        include_in_quotation_pdf: !!terms.include_in_quotation_pdf,
      };
      const { data } = await api.put('/report-settings/terms-of-trade', payload);
      setTerms(data.terms);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (e) {
      alert('Failed to save: ' + (e?.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const reset = async () => {
    if (!window.confirm('Reset every section back to the original legacy text? Your edits will be discarded.')) return;
    setSaving(true);
    try {
      const { data } = await api.post('/report-settings/terms-of-trade/reset');
      setTerms(data.terms);
    } finally {
      setSaving(false);
    }
  };

  const preview = async () => {
    // Batch T Phase 5 (2026-02) — the landscape "reverse-side print"
    // variant is retired. Only the portrait Terms preview remains.
    setPreviewing(true);
    try {
      const { data: latestList } = await api.get('/appraisals?limit=1');
      const latest = (latestList?.items || [])[0];
      if (!latest) {
        alert('No appraisals exist yet to preview against.');
        return;
      }
      // Save current edits first so the preview reflects them
      await save();
      const res = await api.get(`/reports/terms-of-trade/${latest._id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      if (previewObjectUrl.current) URL.revokeObjectURL(previewObjectUrl.current);
      previewObjectUrl.current = url;
      window.open(url, '_blank');
    } catch (e) {
      alert('Preview failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setPreviewing(false);
    }
  };

  if (!terms) return <div className="text-center py-12 text-slate-400">Loading…</div>;

  return (
    <div data-testid="terms-of-trade-editor" className="space-y-5">
      {/* Sticky action bar */}
      <div className="sticky top-0 z-10 -mx-6 -mt-6 px-6 py-3 bg-white border-b border-[#E2E2DF] flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-800 font-[Manrope]">Terms of Trade</h2>
          <p className="text-[11px] text-slate-500">
            Auto-appended to every Quotation PDF. Use{' '}
            <code className="bg-slate-100 px-1 rounded">{'{client_name}'}</code>,{' '}
            <code className="bg-slate-100 px-1 rounded">{'{property_address}'}</code>,{' '}
            <code className="bg-slate-100 px-1 rounded">{'{quote_total}'}</code>,{' '}
            <code className="bg-slate-100 px-1 rounded">{'{appraisal_no}'}</code>,{' '}
            <code className="bg-slate-100 px-1 rounded">{'{date}'}</code> in the Acceptance block.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-slate-600 pr-2 border-r border-[#E2E2DF]">
            <input
              data-testid="terms-include-toggle"
              type="checkbox"
              checked={!!terms.include_in_quotation_pdf}
              onChange={e => setField('include_in_quotation_pdf', e.target.checked)}
            />
            Append to the older one-page Quotation PDF only
          </label>
          <button
            data-testid="terms-preview-btn"
            onClick={preview}
            disabled={previewing}
            className="flex items-center gap-1.5 px-3 py-2 border border-[#E2E2DF] rounded-md text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            <Eye size={14} /> {previewing ? 'Preparing…' : 'Preview PDF'}
          </button>
          {/* Batch T Phase 5 — "Reverse-side print" button removed
              (landscape duplex Terms retired for Go-Live). */}
          <button
            data-testid="terms-reset-btn"
            onClick={reset}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-2 border border-[#E2E2DF] rounded-md text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            title="Restore the original legacy text"
          >
            <RotateCcw size={14} /> Reset to defaults
          </button>
          <button
            data-testid="terms-save-btn"
            onClick={save}
            disabled={saving}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold transition-all ${savedFlash ? 'bg-green-500 text-white' : 'bg-[#D35400] hover:bg-[#A04000] text-white'} disabled:opacity-50`}
          >
            <Save size={14} /> {saving ? 'Saving…' : savedFlash ? 'Saved!' : 'Save All'}
          </button>
        </div>
      </div>

      {/* Title + intro */}
      <div className="grid grid-cols-1 gap-3 bg-slate-50/60 border border-[#E2E2DF] rounded-md p-4">
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Document Title</label>
          <input
            data-testid="terms-title"
            value={terms.title || ''}
            onChange={e => setField('title', e.target.value)}
            className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]"
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Intro Paragraph <span className="text-slate-400 normal-case font-normal">(optional)</span></label>
          <textarea
            data-testid="terms-intro"
            value={terms.intro || ''}
            onChange={e => setField('intro', e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]"
          />
        </div>
      </div>

      {/* Sections list */}
      <div className="space-y-3">
        {terms.sections.map((s, idx) => (
          <div key={idx} data-testid={`terms-section-${idx}`} className="bg-white border border-[#E2E2DF] rounded-md p-4 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400 w-6">#{idx + 1}</span>
              <input
                data-testid={`terms-section-heading-${idx}`}
                value={s.heading}
                onChange={e => setSection(idx, { heading: e.target.value })}
                placeholder="SECTION HEADING"
                className="flex-1 px-3 py-2 border border-[#E2E2DF] rounded-md text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#D35400]"
              />
              <button
                data-testid={`terms-section-up-${idx}`}
                onClick={() => moveSection(idx, -1)}
                disabled={idx === 0}
                className="p-1.5 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                title="Move up"
              ><ChevronUp size={16} /></button>
              <button
                data-testid={`terms-section-down-${idx}`}
                onClick={() => moveSection(idx, 1)}
                disabled={idx === terms.sections.length - 1}
                className="p-1.5 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                title="Move down"
              ><ChevronDown size={16} /></button>
              <button
                data-testid={`terms-section-remove-${idx}`}
                onClick={() => removeSection(idx)}
                className="p-1.5 text-slate-400 hover:text-rose-600"
                title="Remove section"
              ><Trash2 size={15} /></button>
            </div>
            <textarea
              data-testid={`terms-section-body-${idx}`}
              value={s.body}
              onChange={e => setSection(idx, { body: e.target.value })}
              rows={Math.max(3, Math.min(14, (s.body || '').split('\n').length + 1))}
              placeholder="• Start a line with a bullet point for bulleted text&#10;Or use plain lines for body paragraphs"
              className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#D35400]"
            />
            <p className="text-[11px] text-slate-400">
              Tip: start a line with <code className="bg-slate-100 px-1 rounded">• </code> for bullets. Blank lines = paragraph break.
            </p>
          </div>
        ))}
        <button
          data-testid="terms-section-add"
          onClick={addSection}
          className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-[#E2E2DF] rounded-md text-sm text-slate-500 hover:border-[#D35400] hover:text-[#D35400] transition-colors"
        >
          <Plus size={14} /> Add a new section
        </button>
      </div>

      {/* Quotation Acceptance block */}
      <div className="bg-orange-50/40 border border-[#D35400]/30 rounded-md p-4 space-y-3">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <FileDown size={16} className="text-[#D35400]" />
            <h3 className="text-sm font-semibold text-slate-800 font-[Manrope]">Quotation Acceptance Block</h3>
          </div>
          <p className="text-[11px] text-slate-500">
            Renders at the bottom of the Terms pages with the customer's details auto-filled. Use the placeholders shown at the top.
          </p>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Heading</label>
          <input
            data-testid="terms-acceptance-heading"
            value={terms.acceptance_heading || ''}
            onChange={e => setField('acceptance_heading', e.target.value)}
            className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]"
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Subheading</label>
          <input
            data-testid="terms-acceptance-subheading"
            value={terms.acceptance_subheading || ''}
            onChange={e => setField('acceptance_subheading', e.target.value)}
            className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]"
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Body</label>
          <textarea
            data-testid="terms-acceptance-body"
            value={terms.acceptance_body || ''}
            onChange={e => setField('acceptance_body', e.target.value)}
            rows={12}
            className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#D35400]"
          />
        </div>
      </div>
    </div>
  );
}
